
import React, { useState } from 'react';
import { BookingDetails } from '../types';
import Logo from './Logo';

interface Props {
  booking: BookingDetails;
  onBack: () => void;
  onComplete: (isSplitEnabled: boolean) => void;
}

const CheckoutPage: React.FC<Props> = ({ booking, onBack, onComplete }) => {
  const [isCleroEnabled, setIsCleroEnabled] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'idle' | 'processing' | 'confirmed'>('idle');

  const handlePay = () => {
    setCheckoutStatus('processing');
    // Simulate payment processing
    setTimeout(() => {
      setCheckoutStatus('confirmed');
      // Briefly show confirmation before moving to Clero setup or success
      setTimeout(() => {
        onComplete(isCleroEnabled);
      }, 1500);
    }, 2000);
  };

  if (checkoutStatus === 'confirmed') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-500">
        <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center text-white mb-6 shadow-lg scale-110">
          <i className="fas fa-check text-3xl"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">Order confirmed!</h2>
        <p className="text-gray-500 font-medium max-w-xs">
          Your purchase of {booking.title} is secured.
        </p>
        {isCleroEnabled && (
          <div className="mt-8 flex flex-col items-center space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-bounce" />
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-bounce [animation-delay:0.2s]" />
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-bounce [animation-delay:0.4s]" />
            </div>
            <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest">Opening Clero Setup...</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-24 py-12 selection:bg-teal-100 selection:text-teal-900">
      <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-100 z-50 flex items-center px-6 md:px-24">
        <button onClick={onBack} className="flex items-center space-x-2 text-black hover:bg-gray-100 p-2 rounded-full transition-colors">
          <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="block fill-none h-4 w-4 stroke-current stroke-[4] overflow-visible"><g fill="none"><path d="m20 28-11.29289322-11.2928932c-.39052429-.3905243-.39052429-1.0236893 0-1.4142136l11.29289322-11.2928932"></path></g></svg>
        </button>
        <h1 className="text-lg font-semibold text-gray-900 ml-4 tracking-tight">Confirm and pay</h1>
      </div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-16">
        <div className="lg:col-span-3 space-y-12">
          <section className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Your order</h2>
            <div className="space-y-3">
              <div className="p-5 border border-gray-100 rounded-2xl flex justify-between items-center bg-white">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Details</p>
                  <p className="text-sm font-medium text-gray-900">{booking.dates}</p>
                </div>
                <button className="text-sm font-semibold text-gray-900 underline">Edit</button>
              </div>
              <div className="p-5 border border-gray-100 rounded-2xl flex justify-between items-center bg-white">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Quantity</p>
                  <p className="text-sm font-medium text-gray-900">4 people</p>
                </div>
                <button className="text-sm font-semibold text-gray-900 underline">Edit</button>
              </div>
            </div>
          </section>

          <section className="space-y-10">
            <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Payment method</h2>
            <div className="p-6 border-2 border-black rounded-2xl flex items-center justify-between bg-white shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="w-5 h-5 rounded-full border-2 border-black flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-black rounded-full" />
                </div>
                <p className="font-semibold text-gray-900">Credit or debit card</p>
              </div>
              <div className="flex space-x-3 text-2xl opacity-10">
                <i className="fab fa-cc-visa"></i>
                <i className="fab fa-cc-mastercard"></i>
              </div>
            </div>

            <div className="space-y-4 text-center">
              <button
                onClick={handlePay}
                disabled={checkoutStatus !== 'idle'}
                className="w-full bg-black text-white py-4 rounded-xl font-bold text-xl hover:bg-gray-800 transition-all shadow-lg flex items-center justify-center space-x-3 disabled:opacity-70"
              >
                {checkoutStatus === 'processing' ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    <span>Confirming...</span>
                  </>
                ) : (
                  <span>Pay Now</span>
                )}
              </button>
              {isCleroEnabled && (
                <p className="text-xs text-gray-400 font-medium px-8 leading-relaxed animate-in fade-in">
                  You will be redirected to set up your group split automatically after payment.
                </p>
              )}
            </div>
          </section>
        </div>

        <div className="lg:col-span-2 relative">
          <div className="sticky top-24 border border-gray-100 rounded-3xl p-8 space-y-8 bg-white shadow-sm">
            <div className="flex space-x-4">
              <div className="w-20 h-20 bg-gray-50 flex items-center justify-center rounded-full border border-gray-100">
                <i className="fas fa-shopping-bag text-gray-200 text-2xl"></i>
              </div>
              <div className="flex flex-col justify-between py-0.5">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Selected Item</p>
                  <p className="text-sm font-semibold text-gray-900 leading-tight">{booking.title}</p>
                </div>
                <div className="flex items-center text-xs font-semibold text-gray-900">
                  <i className="fas fa-shield-check mr-1 text-teal-500"></i> Clero Protected
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-gray-50">
              <h3 className="text-base font-semibold text-gray-900">Price details</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm text-gray-500 font-medium">
                  <span className="underline">Base Price</span>
                  <span>$2,250</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 font-medium">
                  <span className="underline">Taxes</span>
                  <span>$150</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 font-medium">
                  <span className="underline">Processing</span>
                  <span>$89</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between font-semibold text-gray-900 text-xl tracking-tight pt-6 border-t border-gray-100">
              <span>Total (USD)</span>
              <span>$2,489</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
